import { createSlice } from '@reduxjs/toolkit'
import ad from 'api/axios/foka/ad'
import { createAxiosThunk } from 'utils/reduxUtil'
import moment from 'moment/moment'
import { everyTime, toBit, toInt } from '../../../components/TimeTable'
import { CampaignTypes } from '../../Codes'

const toCommand = (adGroup, campaignType) => {
  const {
    name,
    placementType,
    placementCategories,
    deviceSettingType,
    deviceTypes,
    dailyBudget,
    exceptionKeywords,
    keywordExtensionYn,
    startDate,
    endDate,
    endDateDisabled,
    impressionAll,
    weeklySchedule,
    maxBiddingAmount,
    talkChannelRecommendKeywordYn,
    customContentMatchingMaxBiddingAmountYn,
    contentMatchingMaxBiddingAmount,
    pcWeight,
    mobileWeight,
    keywordExtensionWeight,
  } = adGroup
  const baseCommand = {
    name,
    placementType,
    placementCategories,
    deviceSettingType,
    deviceTypes,
    dailyBudget,
    exceptionKeywords,
    keywordExtensionYn,
    startDate,
    endDate: endDateDisabled ? null : endDate,
    weeklySchedule: toInt(impressionAll ? everyTime : weeklySchedule),
    maxBiddingAmount,
  }
  return campaignType === CampaignTypes.TALK_CHANNEL
    ? {
        ...baseCommand,
        placementType: 'SETTING',
        placementCategories: ['TALK_CHANNEL_SEARCH_KAKAO_TALK'],
        deviceSettingType: 'SETTING',
        deviceTypes: ['MOBILE'],
        talkChannelRecommendKeywordYn,
      }
    : {
        ...baseCommand,
        customContentMatchingMaxBiddingAmountYn,
        contentMatchingMaxBiddingAmount:
          customContentMatchingMaxBiddingAmountYn === 'Y'
            ? contentMatchingMaxBiddingAmount
            : null,
        pcWeight,
        mobileWeight,
        keywordExtensionWeight,
      }
}
export const createAdGroup = createAxiosThunk(
  'adGroup/createAdGroup',
  (noArgs, { getState }) => {
    const {
      campaign: {
        form: { id: campaignId, campaignType },
      },
      adGroup: { form: adGroup },
    } = getState().ad
    const createAdGroupCommand = toCommand(adGroup, campaignType)
    return ad.postAdGroup(campaignType, {
      type: campaignType,
      campaignId,
      ...createAdGroupCommand,
    })
  }
)

export const updateAdGroup = createAxiosThunk(
  'adGroup/updateAdGroup',
  (noArgs, { getState }) => {
    const {
      campaign: {
        form: { campaignType },
      },
      adGroup: { form: adGroup },
    } = getState().ad
    const updateAdGroupCommand = toCommand(adGroup, campaignType)
    return ad.putAdGroup(campaignType, {
      adGroupId: adGroup.id,
      ...updateAdGroupCommand,
    })
  }
)

export const fetchAdGroup = createAxiosThunk(
  'adGroup/fetchAdGroup',
  adGroupId => ad.getAdGroup(adGroupId)
)

const initialState = {
  form: {
    id: null,
    campaignId: null,
    name: '',
    placementType: 'ALL',
    placementCategories: [],
    deviceSettingType: 'ALL',
    deviceTypes: [],
    exceptionKeywords: [],
    keywordExtensionYn: 'Y',
    talkChannelRecommendKeywordYn: 'N',
    maxBiddingAmount: 300,
    customContentMatchingMaxBiddingAmountYn: 'N',
    contentMatchingMaxBiddingAmount: '',
    pcWeight: 100,
    mobileWeight: 100,
    keywordExtensionWeight: 100,
    dailyBudget: 100000,
    startDate: moment().format(moment.HTML5_FMT.DATE),
    endDate: '',
    weeklySchedule: everyTime,
    endDateDisabled: true,
    impressionAll: true,
    beforeContentMatchingMaxBiddingAmount: undefined,
    beforeCustomContentMatchingMaxBiddingAmountYn: undefined,
    beforeMaxBiddingAmount: undefined,
    beforeDailyBudget: undefined,
  },
  isLoading: false,
}
const adGroupSlice = createSlice({
  name: 'adGroup',
  initialState,

  reducers: {
    reset: () => initialState,
    setName: (state, { payload: name }) => {
      state.form.name = name
    },
    setPlacementType: (state, { payload: placementType }) => {
      state.form.placementType = placementType
      if (placementType === 'SETTING') {
        state.form.placementCategories = [
          'PREMIUM_LINK_SEARCH_KAKAO',
          'PREMIUM_LINK_CONTENT_KAKAO',
          'PREMIUM_LINK_CONTENT_PARTNER',
        ]
      }
    },
    addPlacementCategories: (state, { payload: placementCategories }) => {
      placementCategories.forEach(placementCategory => {
        const index = state.form.placementCategories.indexOf(placementCategory)
        if (index === -1) {
          state.form.placementCategories.push(placementCategory)
        }
      })
    },
    removePlacementCategories: (state, { payload: placementCategories }) => {
      placementCategories.forEach(placementCategory => {
        const index = state.form.placementCategories.indexOf(placementCategory)
        if (index !== -1) {
          state.form.placementCategories.splice(index, 1)
        }
      })
    },
    togglePlacementCategories: (state, { payload: placementCategories }) => {
      placementCategories.forEach(placementCategory => {
        const index = state.form.placementCategories.indexOf(placementCategory)
        if (index === -1) {
          state.form.placementCategories.push(placementCategory)
        } else {
          state.form.placementCategories.splice(index, 1)
        }
      })
    },
    setDeviceSettingType: (state, { payload: deviceSettingType }) => {
      state.form.deviceSettingType = deviceSettingType
      if (deviceSettingType === 'SETTING') {
        state.form.deviceTypes = ['MOBILE', 'PC']
      }
    },
    toggleDeviceType: (state, { payload: deviceType }) => {
      const index = state.form.deviceTypes.indexOf(deviceType)
      if (index === -1) {
        state.form.deviceTypes.push(deviceType)
      } else {
        state.form.deviceTypes.splice(index, 1)
      }
    },
    setKeywordExtensionYn: (state, { payload: keywordExtensionYn }) => {
      state.form.keywordExtensionYn = keywordExtensionYn
    },
    setExceptionKeywords: (state, { payload: exceptionKeywords }) => {
      state.form.exceptionKeywords = exceptionKeywords
    },
    setMaxBiddingAmount: (state, { payload: maxBiddingAmount }) => {
      state.form.maxBiddingAmount = maxBiddingAmount
    },
    setDailyBudget: (state, { payload: dailyBudget }) => {
      state.form.dailyBudget = dailyBudget
    },
    setWeight: (state, { payload: { type, value } }) => {
      state.form[type] = value
    },
    setContentMatchingMaxBiddingAmount: (
      state,
      { payload: contentMatchingMaxBiddingAmount }
    ) => {
      state.form.contentMatchingMaxBiddingAmount =
        contentMatchingMaxBiddingAmount
    },
    setCustomContentMatchingMaxBiddingAmountYn: (
      state,
      { payload: customContentMatchingMaxBiddingAmountYn }
    ) => {
      state.form.customContentMatchingMaxBiddingAmountYn =
        customContentMatchingMaxBiddingAmountYn
      state.form.contentMatchingMaxBiddingAmount =
        customContentMatchingMaxBiddingAmountYn === 'Y'
          ? state.form.maxBiddingAmount
          : ''
    },
    setStartDate: (state, { payload: startDate }) => {
      state.form.startDate = startDate
    },
    setEndDate: (state, { payload: endDate }) => {
      state.form.endDate = endDate
    },
    setImpressionAll: (state, { payload: impressionAll }) => {
      state.form.impressionAll = impressionAll
    },
    setWeeklySchedule: (state, { payload: weeklySchedule }) => {
      state.form.weeklySchedule = weeklySchedule
    },
    setEndDateDisabled: (state, { payload: endDateDisabled }) => {
      state.form.endDateDisabled = endDateDisabled
      if (endDateDisabled) state.form.endDate = null
    },
  },

  extraReducers: builder => {
    builder.addCase(createAdGroup.pending, state => {
      state.isLoading = true
    })

    builder.addCase(createAdGroup.fulfilled, state => {
      state.isLoading = false
    })

    builder.addCase(createAdGroup.rejected, state => {
      state.isLoading = false
    })

    builder.addCase(updateAdGroup.pending, state => {
      state.isLoading = true
    })

    builder.addCase(updateAdGroup.fulfilled, state => {
      state.isLoading = false
    })

    builder.addCase(updateAdGroup.rejected, state => {
      state.isLoading = false
    })

    builder.addCase(fetchAdGroup.pending, state => {
      state.isLoading = true
    })

    builder.addCase(
      fetchAdGroup.fulfilled,
      (state, { payload: { adGroupDto } }) => {
        const {
          placement,
          deviceSetting,
          exceptionKeywords,
          biddingStrategy,
          dailyBudget,
          impressionSchedule,
          status,
          adAccountId,
          adGroupType,
          createdDate,
          updatedDate,
          deletedDate,
          lastModifier,
          ...rest
        } = adGroupDto
        const maxBiddingAmount = biddingStrategy.maxBiddingAmount || 300
        const customContentMatchingMaxBiddingAmountYn =
          biddingStrategy.customContentMatchingMaxBiddingAmountYn || 'N'
        const contentMatchingMaxBiddingAmount =
          biddingStrategy.customContentMatchingMaxBiddingAmountYn === 'Y'
            ? biddingStrategy.contentMatchingMaxBiddingAmount || ''
            : ''
        const { pcWeight, mobileWeight, keywordExtensionWeight } =
          biddingStrategy
        const dailyBudgetAmount = dailyBudget.amount || 100000
        state.form = {
          ...rest,
          placementType: placement.type,
          placementCategories: placement.categories
            ? placement.categories
                .map(category => category.name)
                .filter(name => name !== 'PREMIUM_LINK_SEARCH_PARTNER')
            : [],
          deviceSettingType: deviceSetting.type,
          deviceTypes: deviceSetting.deviceTypes,
          exceptionKeywords: exceptionKeywords ? exceptionKeywords.sort() : [],
          maxBiddingAmount,
          customContentMatchingMaxBiddingAmountYn,
          contentMatchingMaxBiddingAmount,
          pcWeight,
          mobileWeight,
          keywordExtensionWeight,
          dailyBudget: dailyBudgetAmount,
          startDate:
            impressionSchedule.startDate ||
            moment().format(moment.HTML5_FMT.DATE),
          endDate: impressionSchedule.endDate || '',
          weeklySchedule: impressionSchedule.weeklySchedule
            ? toBit(impressionSchedule.weeklySchedule)
            : everyTime,
          endDateDisabled: !impressionSchedule.endDate,
          impressionAll:
            !impressionSchedule.weeklySchedule ||
            toBit(impressionSchedule.weeklySchedule) === everyTime,
          beforeContentMatchingMaxBiddingAmount:
            contentMatchingMaxBiddingAmount,
          beforeCustomContentMatchingMaxBiddingAmountYn:
            customContentMatchingMaxBiddingAmountYn,
          beforeMaxBiddingAmount: maxBiddingAmount,
          beforeDailyBudget: dailyBudgetAmount,
        }
        state.isLoading = false
      }
    )

    builder.addCase(fetchAdGroup.rejected, state => {
      state.isLoading = false
    })
  },
})
const { actions, reducer } = adGroupSlice
export const {
  reset,
  setName,
  setPlacementType,
  togglePlacementCategories,
  setDeviceSettingType,
  addPlacementCategories,
  removePlacementCategories,
  toggleDeviceType,
  setKeywordExtensionYn,
  setExceptionKeywords,
  setMaxBiddingAmount,
  setDailyBudget,
  setWeight,
  setContentMatchingMaxBiddingAmount,
  setCustomContentMatchingMaxBiddingAmountYn,
  setStartDate,
  setEndDate,
  setImpressionAll,
  setWeeklySchedule,
  setEndDateDisabled,
} = actions
export default reducer
